// @flow
import React from 'react';
import type {Node} from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconNames,
  Box,
  Popover,
  IconButton,
} from '@wellstone-solutions/web';

import useMediaQuery from '@mui/material/useMediaQuery';
import Slide from '@mui/material/Slide';

import {useTheme} from '@mui/material/styles';

type PropsType = {
  children: Node,
  anchorEl: HTMLElement | null,
  onClose: () => void,
  title?: string,
  closeButton?: boolean,
  transitionDirection?: string,
  transitionTimeout?: number,
  fullHeight?: boolean,
};

const POPOVER_WIDTH = '315px';
const MAX_DIALOG_WIDTH = '900px';

const Transition = React.forwardRef(function Transition(props, ref) {
  const {direction = 'right', ...other} = props;
  return <Slide direction={direction} ref={ref} {...other} />;
});

export const SiteHeaderDrawer = ({
  children,
  onClose,
  anchorEl,
  title = '',
  fullHeight = false,
  transitionDirection = 'right',
  transitionTimeout = 200,
  closeButton = true,
}: PropsType): Node => {
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const showCloseButton = Boolean(closeButton);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleClose = () => {
    onClose();
  };

  return isMobile ? (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      PaperProps={{sx: style.dialog_paper}}
      TransitionComponent={Transition}
      TransitionProps={{
        direction: transitionDirection,
        timeout: transitionTimeout,
      }}
      sx={
        fullHeight
          ? {...style.dialog, ...style.full_height_dialog}
          : style.dialog
      }
      BackdropProps={{
        sx: {bgcolor: 'transparent'},
      }}
      data-testid="header-drawer-mobile">
      <DialogTitle sx={{padding: 0}}>
        <Box style={style.drawer_header}>
          {title && <Box data-testid="drawer-title">{title}</Box>}
          <Box sx={{mt: title ? 0 : 2}}>
            <IconButton
              onClick={handleClose}
              icon={IconNames.Xmark}
              iconProps={{name: IconNames.Xmark, style: {strokeWidth: 2}}}
              data-testid="close-dialog"
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{padding: 0}}>{children}</DialogContent>
    </Dialog>
  ) : (
    <Popover
      data-testid="header-drawer-desktop"
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      sx={style.popover}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}>
      <Box style={style.drawer_header}>
        <Box>{title}</Box>
        <Box>
          {showCloseButton && (
            <IconButton
              onClick={handleClose}
              icon={IconNames.Xmark}></IconButton>
          )}
        </Box>
      </Box>
      <Box sx={style.drawer_content}>{children}</Box>
    </Popover>
  );
};

const style = {
  dialog: {
    position: 'fixed',
    top: '80px',
    maxWidth: '100vw',
    padding: 0,
    margin: 0,
  },
  full_height_dialog: {
    top: '0px',
    width: 'calc(100vw - 20px)',
    maxWidth: '350px',
  },
  dialog_paper: {
    width: '100vw',
    maxWidth: MAX_DIALOG_WIDTH,
    padding: 0,
    margin: 0,
  },
  popover: {
    '& .MuiPopover-paper': {
      borderRadius: '4px',
      width: POPOVER_WIDTH,
      padding: 0,
      marginLeft: '-30px',
      marginTop: '-20px',
    },
  },
  drawer_header: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '32px',
    letterSpacing: '0.25px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
  },
  drawer_content: {
    padding: '0px',
  },
};
